import React from "react";
import { LOGO_URL } from "@/components/Logo";
import { money, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/PrefsContext";

/**
 * Single invoice component used for Preview, Print, and PDF (browser print-to-PDF).
 * Renders inside #invoice-print-area so print CSS isolates it.
 */
export default function InvoiceDocument({ invoice }) {
  const { settings } = useAuth();
  const { t } = useI18n();
  if (!invoice) return null;

  const company = settings || {};
  const logo = company.logo_url || LOGO_URL;
  const items = invoice.line_items || [];
  const isPaid = Number(invoice.balance_due || 0) <= 0.001 && Number(invoice.total || 0) > 0;
  const credit = Number(invoice.credit_balance || 0);

  return (
    <div
      id="invoice-print-area"
      className="bg-white mx-auto"
      style={{ width: "8.5in", maxWidth: "100%", padding: "0.55in", color: "#121212", position: "relative" }}
      data-testid="invoice-document"
    >
      {/* PAID stamp */}
      {isPaid && (
        <div
          className="paid-stamp"
          style={{
            position: "absolute",
            top: "44%",
            left: "50%",
            transform: "translate(-50%, -50%) rotate(-8deg)",
            fontSize: "54px",
            padding: "10px 36px",
            pointerEvents: "none",
            zIndex: 5,
            fontFamily: "'Playfair Display', serif",
          }}
          data-testid="paid-stamp"
        >
          {t("Paid In Full")}
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-start pb-6 border-b-2" style={{ borderColor: "#5A189A" }}>
        <div className="flex items-center gap-4">
          <img src={logo} alt="logo" style={{ width: 88, height: 88 }} className="rounded-full object-cover" />
          <div>
            <div className="font-display text-2xl font-bold" style={{ color: "#5A189A" }}>
              {company.company_name || "Magic Touch Professional Cleaning"}
            </div>
            <div className="text-xs mt-1" style={{ color: "#52525B" }}>
              {company.address}<br />
              {company.phone} · {company.email}<br />
              {company.website}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-3xl font-bold tracking-tight" style={{ color: "#D4AF37" }}>
            {t("INVOICE")}
          </div>
          <div className="text-sm font-semibold mt-1" style={{ color: "#121212" }}>
            {invoice.invoice_number}
          </div>
          <div className="text-xs mt-2" style={{ color: "#52525B" }}>
            {t("Issue Date")}: {formatDate(invoice.issue_date) || "-"}<br />
            {t("Due Date")}: {formatDate(invoice.due_date) || "-"}
          </div>
        </div>
      </div>

      {/* Bill To */}
      <div className="flex justify-between mt-6">
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#D4AF37" }}>
            {t("Bill To")}
          </div>
          <div className="font-semibold text-sm" style={{ color: "#121212" }}>{invoice.customer_name}</div>
          <div className="text-xs mt-1" style={{ color: "#52525B" }}>
            {invoice.address}{invoice.address && <br />}
            {[invoice.city, invoice.state, invoice.zip].filter(Boolean).join(", ")}<br />
            {invoice.customer_phone}{invoice.customer_phone && <br />}
            {invoice.customer_email}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#D4AF37" }}>
            {t("Status")}
          </div>
          <div
            className="inline-block px-3 py-1 rounded-full text-xs font-bold"
            style={{
              backgroundColor: isPaid ? "#dcfce7" : "#fef3c7",
              color: isPaid ? "#16a34a" : "#b45309",
            }}
          >
            {t(invoice.status)}
          </div>
        </div>
      </div>

      {/* Line items */}
      <table className="w-full mt-6 text-sm" style={{ borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "#5A189A", color: "#fff" }}>
            <th className="text-left py-2.5 px-3 rounded-l-lg" style={{ fontWeight: 600 }}>{t("Service")}</th>
            <th className="text-left py-2.5 px-3">{t("Description")}</th>
            <th className="text-center py-2.5 px-3">{t("Qty")}</th>
            <th className="text-right py-2.5 px-3">{t("Rate")}</th>
            <th className="text-right py-2.5 px-3 rounded-r-lg">{t("Amount")}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((li, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #efeaf6" }}>
              <td className="py-2.5 px-3 font-medium">{li.service}</td>
              <td className="py-2.5 px-3" style={{ color: "#52525B" }}>{li.description}</td>
              <td className="py-2.5 px-3 text-center">{li.qty}</td>
              <td className="py-2.5 px-3 text-right">{money(li.price)}</td>
              <td className="py-2.5 px-3 text-right font-medium">{money(li.qty * li.price)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="flex justify-end mt-6">
        <div style={{ width: "300px" }}>
          <Row label={t("Subtotal")} value={money(invoice.subtotal)} />
          <Row label={`${t("Tax")} (${invoice.tax_rate || 0}%)`} value={money(invoice.tax)} />
          {Number(invoice.discount) > 0 && <Row label={t("Discount")} value={`- ${money(invoice.discount)}`} />}
          {Number(invoice.deposit) > 0 && <Row label={t("Deposit")} value={`- ${money(invoice.deposit)}`} />}
          {Number(invoice.amount_paid) > 0 && <Row label={t("Amount Paid")} value={`- ${money(invoice.amount_paid)}`} />}
          <div className="flex justify-between items-center mt-2 pt-3 px-3 rounded-lg"
            style={{ background: "#5A189A", color: "#fff" }}>
            <span className="font-semibold pb-3">{credit > 0 ? t("Credit Balance") : t("Balance Due")}</span>
            <span className="font-bold text-lg pb-3 font-display">
              {credit > 0 ? money(credit) : money(invoice.balance_due)}
            </span>
          </div>
        </div>
      </div>

      {/* Notes / Footer */}
      <div className="mt-10 pt-5 border-t" style={{ borderColor: "#efeaf6" }}>
        {invoice.notes && (
          <div className="text-xs mb-3" style={{ color: "#52525B" }}>
            <span className="font-semibold" style={{ color: "#5A189A" }}>{t("Notes")}: </span>
            {invoice.notes}
          </div>
        )}
        <div className="text-center text-xs" style={{ color: "#9b8bb5" }}>
          <div className="font-display text-sm" style={{ color: "#D4AF37" }}>
            {t("Thank you for choosing")} {company.company_name || "Magic Touch Professional Cleaning"}
          </div>
          <div className="mt-1">{company.phone} · {company.email} · {company.website}</div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-center py-1.5 px-3 text-sm">
      <span style={{ color: "#52525B" }}>{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
